import { useState } from 'react';

interface Building {
  name: string;
  address: string;
  neighborhood: string;
  units: string;
}

const featuredBuildings: Building[] = [
  {
    name: "The Residences on Georgia",
    address: "1288 W Georgia St",
    neighborhood: "Downtown Vancouver",
    units: "200+ units"
  },
  {
    name: "Shangri-La Residences",
    address: "1128 W Georgia St",
    neighborhood: "Downtown Vancouver",
    units: "150+ units"
  },
  {
    name: "Trump Tower Vancouver",
    address: "1151 W Georgia St",
    neighborhood: "Downtown Vancouver",
    units: "147 units"
  },
  {
    name: "Fairmont Pacific Rim Residences",
    address: "1011 W Cordova St",
    neighborhood: "Coal Harbour",
    units: "120+ units"
  },
  {
    name: "The Melville",
    address: "1288 Melville St",
    neighborhood: "West End",
    units: "180+ units"
  },
  {
    name: "Azura at Capitol Hill",
    address: "5058 Cambie St",
    neighborhood: "Cambie Village",
    units: "90+ units"
  },
  {
    name: "The Beasley",
    address: "1265 Barclay St",
    neighborhood: "West End",
    units: "160+ units"
  },
  {
    name: "Jameson House",
    address: "838 W Hastings St",
    neighborhood: "Downtown Vancouver",
    units: "130+ units"
  }
];

export function FeaturedBuildings() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || 'Request for full building list (50+ buildings)'
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d6aedslbd17lru29lpdg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '' });
        setTimeout(() => {
          setShowModal(false);
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="mt-20 bg-gradient-to-br from-[#F8F8F8] to-white rounded-2xl p-8 md:p-12">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#882629]/10 rounded-full mb-4">
            <i className="ri-building-line text-3xl text-[#882629]"></i>
          </div>
          <h2 className="font-['Fraunces'] text-3xl md:text-4xl font-bold text-[#505A5A] mb-3">
            Green-Lit Buildings
          </h2>
          <p className="text-base text-[#505A5A]/70 max-w-2xl mx-auto">
            Here are some of the verified buildings where short-term rentals are permitted. We maintain a comprehensive database of 50+ buildings across Metro Vancouver.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {featuredBuildings.map((building, index) => (
            <div 
              key={index}
              className="bg-white rounded-lg p-5 border border-[#F0F0F0] hover:border-[#882629]/30 hover:shadow-md transition-all duration-300"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-[#882629]/10 rounded-lg flex-shrink-0">
                  <i className="ri-checkbox-circle-fill text-[#882629] text-lg"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-['Fraunces'] text-base font-semibold text-[#505A5A] mb-1 leading-tight">
                    {building.name}
                  </h3>
                  <p className="text-xs text-[#505A5A]/60 mb-1">
                    <i className="ri-map-pin-2-fill text-[#882629] mr-1"></i>
                    {building.address}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-[#505A5A]/70 font-medium">
                      {building.neighborhood}
                    </span>
                    <span className="text-xs text-[#882629] bg-[#882629]/5 px-2 py-1 rounded-full">
                      {building.units}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center bg-white rounded-xl p-8 border-2 border-dashed border-[#882629]/20">
          <div className="max-w-xl mx-auto">
            <h3 className="font-['Fraunces'] text-xl font-bold text-[#505A5A] mb-2">
              Want the Complete List?
            </h3>
            <p className="text-sm text-[#505A5A]/70 mb-5">
              Get access to our full database of 50+ verified STR-friendly buildings across Vancouver, Burnaby, North Vancouver, and surrounding areas.
            </p>
            <button
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-2 bg-[#882629] text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-[#6d1e21] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-list-3-line text-lg"></i>
              Request Full Building List
            </button>
            <p className="text-xs text-[#505A5A]/50 mt-3">
              Free • Updated Monthly • Includes Strata Contact Info
            </p>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-8 relative animate-[fadeIn_0.2s_ease-out]">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-[#505A5A]/40 hover:text-[#505A5A] transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#882629]/10 rounded-full mb-3">
                <i className="ri-building-line text-2xl text-[#882629]"></i>
              </div>
              <h3 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A] mb-2">
                Request Full Building List
              </h3>
              <p className="text-sm text-[#505A5A]/70">
                Enter your details and we'll send you the complete list of 50+ STR-friendly buildings.
              </p>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form id="building-list-request">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#505A5A] mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#F0F0F0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#882629]/20 focus:border-[#882629]"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#505A5A] mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#F0F0F0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#882629]/20 focus:border-[#882629]"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-[#505A5A] mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#F0F0F0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#882629]/20 focus:border-[#882629]"
                    placeholder="(604) 555-0123"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#505A5A] mb-1">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-2.5 border border-[#F0F0F0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#882629]/20 focus:border-[#882629] resize-none"
                    placeholder="Any specific neighborhoods or building types you're interested in?"
                  />
                  <p className="text-xs text-[#505A5A]/50 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm text-green-800">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  <span>Request sent! Check your email for the building list.</span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-800">
                  <i className="ri-error-warning-fill text-lg"></i>
                  <span>Something went wrong. Please try again.</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 bg-[#882629] text-white py-3 rounded-lg font-semibold text-sm hover:bg-[#6d1e21] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-fill"></i>
                    Send Me The List
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
